@let open = myChainstore.panelOpen();
<div
  class="fixed inset-0 z-50 transition-opacity duration-200"
  [class.opacity-0]="!open"
  [class.opacity-100]="open"
  [class.pointer-events-none]="!open"
  [attr.aria-hidden]="!open"
>
  <button
    class="bg-dark-main focus-visible:ring-primary-main fixed inset-0 transition-opacity duration-200 focus-visible:ring-2 focus-visible:outline-none focus-visible:ring-inset"
    [class.opacity-0]="!open"
    [class.opacity-50]="open"
    [attr.tabindex]="open ? null : -1"
    [attr.aria-label]="'header.mainbar.myChainstore.close' | translate"
    (click)="myChainstore.closePanel()"
  ></button>
  <aside
    #panel
    tabindex="-1"
    class="bg-light-main text-light-contrast absolute top-0 right-0 flex h-full w-full flex-col overflow-y-auto shadow-2xl transition-transform duration-200 ease-out sm:max-w-3xl"
    [class.translate-x-full]="!open"
    [class.translate-x-0]="open"
    [attr.inert]="!open ? '' : null"
    role="dialog"
    aria-modal="true"
    [attr.aria-label]="'header.mainbar.myChainstore.title' | translate"
  >
    <header class="flex items-start justify-between gap-4 px-6 pt-6">
      <h3>{{ 'header.mainbar.myChainstore.title' | translate }}</h3>
      <button
        class="hover:text-secondary-main focus-visible:ring-primary-main inline-flex size-8 shrink-0 items-center justify-center bg-transparent transition-colors focus-visible:rounded focus-visible:ring-2 focus-visible:outline-none"
        [attr.aria-label]="'header.mainbar.myChainstore.close' | translate"
        (click)="myChainstore.closePanel()"
      >
        <gs-icon iconName="x" iconWeight="light" class="text-2xl"></gs-icon>
      </button>
    </header>

    @if (myChainstore.showSearch()) {
      <p class="px-6 pt-4">{{ 'header.mainbar.myChainstore.description' | translate }}</p>
    } @else {
      <p class="px-6 pt-4">{{ 'header.mainbar.myChainstore.listDescription' | translate }}</p>
    }

    @if (myChainstore.showSearch()) {
      <search class="px-6 pt-6">
        <div
          class="border-medium-main focus-within:border-secondary-main flex w-full overflow-hidden border"
          [ngClass]="[searchBarBorderRadiusClass]"
        >
          <div class="relative flex-1">
            <input
              id="myChainstoreSearchInput"
              type="text"
              class="bg-light-main placeholder:text-medium-main h-12 w-full border-0 px-4 py-4 outline-none focus:ring-0"
              [formControl]="searchControl"
              placeholder="{{ 'header.mainbar.myChainstore.searchPlaceholder' | translate }}"
            />
            <label for="myChainstoreSearchInput" class="sr-only">
              {{ 'header.mainbar.myChainstore.searchLabel' | translate }}
            </label>
            @if (searchControl.value) {
              <button
                type="button"
                class="text-medium-main hover:text-medium-shade absolute top-1/2 right-3 -translate-y-1/2 transform text-lg"
                [attr.aria-label]="'header.mainbar.myChainstore.searchClear' | translate"
                (click)="clearSearch()"
              >
                <gs-icon iconName="x" iconWeight="light"></gs-icon>
              </button>
            }
          </div>
          <button
            type="button"
            class="inline-flex h-12 w-12 items-center justify-center !rounded-none !border-0 text-lg font-normal transition-colors focus:outline-none"
            [ngClass]="[actionButtonClasses]"
            [attr.aria-label]="'header.mainbar.myChainstore.searchLabel' | translate"
          >
            <gs-icon iconName="magnifying-glass" iconWeight="light"></gs-icon>
          </button>
        </div>
      </search>
    }

    @if (showMap()) {
      <div class="px-6 pt-6">
        <!-- The panel is part of every header, so the map (and @angular/google-maps) loads only once it is opened -->
        @defer (when open) {
          <google-maps-renderer
            [locations]="myChainstore.mapLocations()"
            [consentTitle]="'header.mainbar.myChainstore.title' | translate"
            [consentMessage]="'header.mainbar.myChainstore.cookieMessage' | translate"
            [mapHeight]="{ default: 260 }"
          ></google-maps-renderer>
        }
      </div>
    }

    @if (myChainstore.loading()) {
      <output class="px-6 py-6" aria-live="polite" aria-busy="true">
        <p>{{ 'header.mainbar.myChainstore.loading' | translate }}</p>
      </output>
    } @else {
      @let stores = myChainstore.filteredStores();
      @if (stores.length === 0) {
        <p class="px-6 py-6">{{ 'header.mainbar.myChainstore.noResults' | translate }}</p>
      } @else {
        <ul class="flex flex-col gap-4 px-6 py-6">
          @for (store of stores; track trackStore(store)) {
            @let storeKey = trackStore(store);
            @let isCurrent = store.iD === myChainstore.current()?.chainStoreID;
            @let isExpanded = expandedStore() === storeKey;
            <li class="border-medium-main flex flex-col gap-4 border p-5">
              <p class="font-bold">{{ store.name }}</p>
              @let addressText = myChainstore.getAddressText(store);
              @if (addressText) {
                <div class="flex flex-row items-center gap-3">
                  <gs-icon class="text-secondary-main" iconName="map-pin" iconWeight="light"></gs-icon>
                  <address class="p not-italic">{{ addressText }}</address>
                </div>
              }
              @if (store.phone) {
                <div class="flex flex-row items-center gap-3">
                  <gs-icon class="text-secondary-main" iconName="phone" iconWeight="light"></gs-icon>
                  <a class="p hover:underline" [href]="'tel:' + store.phone">{{ store.phone }}</a>
                </div>
              }
              @if (store.eMailAddress) {
                <div class="flex flex-row items-center gap-3">
                  <gs-icon class="text-secondary-main" iconName="envelope" iconWeight="light"></gs-icon>
                  <a class="p hover:underline" [href]="'mailto:' + store.eMailAddress">{{ store.eMailAddress }}</a>
                </div>
              }
              @let todaysOpeningTime = getTodaysOpeningTime(store);
              @if (todaysOpeningTime) {
                @if (hasOpeningHours(store)) {
                  <button
                    class="focus-visible:ring-primary-main flex cursor-pointer flex-row items-center gap-3 bg-transparent text-left focus-visible:ring-2 focus-visible:outline-none"
                    [attr.aria-expanded]="isExpanded"
                    (click)="toggleOpeningHours(store)"
                  >
                    <gs-icon class="text-secondary-main" iconName="clock" iconWeight="light"></gs-icon>
                    <span class="p">{{ todaysOpeningTime }}</span>
                    <gs-icon
                      class="text-secondary-main"
                      [iconName]="isExpanded ? 'caret-up' : 'caret-down'"
                      iconWeight="light"
                    ></gs-icon>
                  </button>
                  @if (isExpanded) {
                    <opening-hours class="block pl-8" [openingHours]="getOpeningHours(store)"></opening-hours>
                  }
                } @else {
                  <div class="flex flex-row items-center gap-3">
                    <gs-icon class="text-secondary-main" iconName="clock" iconWeight="light"></gs-icon>
                    <span class="p">{{ todaysOpeningTime }}</span>
                  </div>
                }
              }
              <div class="flex flex-row flex-wrap items-center gap-4">
                <button
                  [ngClass]="[actionButtonClasses]"
                  [ngStyle]="actionButtonStyles"
                  [disabled]="isCurrent"
                  (click)="myChainstore.select(store)"
                >
                  <gs-icon class="pr-3" iconName="heart" iconWeight="light"></gs-icon>
                  {{ (isCurrent ? 'header.mainbar.myChainstore.selected' : 'header.mainbar.myChainstore.select') | translate }}
                </button>
                @if (store.detailLink) {
                  <a [ngClass]="[actionButtonClasses]" [ngStyle]="actionButtonStyles" [href]="store.detailLink">
                    <gs-icon class="pr-3" iconName="map-pin" iconWeight="light"></gs-icon>
                    {{ 'header.mainbar.myChainstore.detailLink' | translate }}
                  </a>
                }
              </div>
            </li>
          }
        </ul>
      }
    }
  </aside>
</div>
